/**
 * Segment Modal Component
 * Modal for creating and editing segments
 * Supports tabs in create mode: Empty, Favorites, Copy from Matchday, Templates
 */

import React, { useState, useEffect } from 'react';
import { Bus, Plane } from 'lucide-react';
import {
  PlanningSegment,
  SegmentFormData,
  ANCHOR_CONFIGS,
  VEHICLE_CONFIGS,
  TRIP_TYPE_CONFIGS,
  AnchorType,
  VehicleType,
  TripType,
  ClubInfo,
  VehicleAssignment,
  SegmentTemplate,
  PlanningTemplate,
  MatchdayPlanning
} from '../../types/matchdayPlanning';
import VehicleSelectionModal from './VehicleSelectionModal';
import { matchdayPlanningApi } from '../../services/matchdayPlanningApi';

type CreateTab = 'empty' | 'favorites' | 'copy' | 'templates';

interface SegmentModalProps {
  mode: 'create' | 'edit';
  segmentId?: number | null;
  segments: PlanningSegment[];
  planningId?: number;
  onSubmit: (formData: SegmentFormData) => void;
  onClose: () => void;
  onRefreshPlanning?: () => void;
  clubs: ClubInfo[];
  vehicleAssignments?: VehicleAssignment[];
}

const SegmentModal: React.FC<SegmentModalProps> = ({
  mode,
  segmentId,
  segments,
  planningId,
  onSubmit,
  onClose,
  onRefreshPlanning,
  clubs,
  vehicleAssignments = []
}) => {
  const [activeTab, setActiveTab] = useState<CreateTab>('empty');
  const [favorites, setFavorites] = useState<SegmentTemplate[]>([]);
  const [templates, setTemplates] = useState<PlanningTemplate[]>([]);
  const [allPlannings, setAllPlannings] = useState<MatchdayPlanning[]>([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [favInsertPosition, setFavInsertPosition] = useState<'before-game' | 'after-game'>('before-game');
  const [selectedCopyPlanningId, setSelectedCopyPlanningId] = useState<number | undefined>();
  const [savingTemplateMode, setSavingTemplateMode] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');

  // Load data when switching tabs
  useEffect(() => {
    if (mode !== 'create') return;
    const loadTabData = async () => {
      setTabLoading(true);
      try {
        if (activeTab === 'favorites') {
          const data = await matchdayPlanningApi.getFavorites();
          setFavorites(data);
        } else if (activeTab === 'templates') {
          const data = await matchdayPlanningApi.getPlanningTemplates();
          setTemplates(data);
        } else if (activeTab === 'copy') {
          const data = await matchdayPlanningApi.getAllPlannings();
          setAllPlannings(data.filter(p => p.id !== planningId));
        }
      } catch (err) {
        console.error('Error loading tab data:', err);
      } finally {
        setTabLoading(false);
      }
    };
    loadTabData();
  }, [activeTab, mode, planningId]);

  const getInitialFormData = (): SegmentFormData => ({
    location: '',
    scheduledDate: new Date().toISOString().split('T')[0],
    scheduledTime: '12:00',
    anchorType: 'departure',
    vehicleType: vehicleAssignments[0]?.vehicleType || 'bus',
    tripType: 'team',
    stopoverDurationMinutes: 30,
    passengerCount: undefined,
    insertPosition: 'before-game',
    vehicleAssignmentId: vehicleAssignments[0]?.id,
    clubId: undefined,
    customLocation: undefined
  });

  const [formData, setFormData] = useState<SegmentFormData>(getInitialFormData());
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [useClubAddress, setUseClubAddress] = useState(false);

  const getValidVehicleType = (vehicleType: any): VehicleType => {
    const validTypes: VehicleType[] = ['bus', 'minibus', 'car', 'plane', 'train', 'taxi', 'rentalcar', 'ship', 'bicycle', 'onfoot'];
    return vehicleType && validTypes.includes(vehicleType) ? vehicleType : 'bus';
  };

  useEffect(() => {
    if (mode === 'edit' && segmentId) {
      const segment = segments.find(s => s.id === segmentId);
      if (segment) {
        setFormData({
          location: segment.location,
          scheduledDate: segment.scheduledDate
            ? new Date(segment.scheduledDate).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          scheduledTime: segment.scheduledTime?.substring(0, 5) || '12:00',
          anchorType: segment.anchorType || 'departure',
          vehicleType: getValidVehicleType(segment.vehicleType),
          tripType: segment.tripType || 'team',
          stopoverDurationMinutes: segment.stopoverDurationMinutes || 30,
          passengerCount: segment.passengerCount,
          insertPosition: 'before-game',
          vehicleAssignmentId: segment.vehicleAssignmentId,
          clubId: segment.clubId,
          customLocation: segment.customLocation
        });
        setUseClubAddress(!!segment.clubId);
      }
    } else if (mode === 'create') {
      setFormData(getInitialFormData());
      setUseClubAddress(false);
    }
  }, [mode, segmentId, segments, vehicleAssignments]);

  useEffect(() => {
    if (useClubAddress && formData.clubId) {
      const selectedClub = clubs.find(c => c.id === formData.clubId);
      if (selectedClub) {
        // Prefer validated stadiumAddress, fallback to "stadium, city"
        const resolvedAddress = selectedClub.stadiumAddress
          || (selectedClub.stadium && selectedClub.city ? `${selectedClub.stadium}, ${selectedClub.city}` : selectedClub.stadium)
          || undefined;
        setFormData(prev => ({
          ...prev,
          location: `${selectedClub.name} - ${selectedClub.city || ''}`,
          customLocation: resolvedAddress
        }));
      }
    }
  }, [useClubAddress, formData.clubId, clubs]);

  // When vehicleAssignmentId changes, sync vehicleType from the selected assignment
  const handleVehicleAssignmentChange = (id: number | undefined) => {
    const assignment = vehicleAssignments.find(va => va.id === id);
    setFormData(prev => ({
      ...prev,
      vehicleAssignmentId: id,
      vehicleType: assignment?.vehicleType || prev.vehicleType
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.location.trim()) {
      alert('Bitte geben Sie einen Ort ein.');
      return;
    }

    if (!formData.scheduledTime || !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formData.scheduledTime)) {
      alert('Bitte geben Sie eine gültige Zeit ein (HH:MM).');
      return;
    }

    if (formData.anchorType === 'stopover' && (!formData.stopoverDurationMinutes || formData.stopoverDurationMinutes < 5)) {
      alert('Bitte geben Sie eine gültige Aufenthaltsdauer ein (mindestens 5 Minuten).');
      return;
    }

    try {
      onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Fehler beim Speichern des Segments. Bitte versuchen Sie es erneut.');
    }
  };

  const handleInputChange = (field: keyof SegmentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAnchorTypeChange = (anchorType: AnchorType) => {
    if (anchorType === 'main') return;
    setFormData(prev => ({ ...prev, anchorType }));
  };

  const handleVehicleTypeChange = (vehicleType: VehicleType) => {
    setFormData(prev => ({ ...prev, vehicleType }));
    setIsVehicleModalOpen(false);
  };

  const handleTripTypeChange = (tripType: TripType) => {
    setFormData(prev => ({ ...prev, tripType }));
  };

  const handlePositionChange = (position: 'before-game' | 'after-game') => {
    setFormData(prev => ({ ...prev, insertPosition: position }));
  };

  const getAssignmentLabel = (va: VehicleAssignment): string => {
    const vehicleConfig = VEHICLE_CONFIGS[va.vehicleType];
    const type = vehicleConfig?.name || va.vehicleType;
    const vehicle = va.vehicle?.name || va.transportation?.name || '';
    const driver = va.traveler?.fullName || va.partnerDriver?.fullName || '';
    const parts = [type, vehicle, driver].filter(Boolean);
    return parts.join(' – ');
  };

  const handleLoadFavoriteIntoForm = (fav: SegmentTemplate) => {
    setFormData({
      location: fav.location || '',
      scheduledDate: new Date().toISOString().split('T')[0],
      scheduledTime: '12:00',
      anchorType: fav.anchorType || 'departure',
      vehicleType: getValidVehicleType(fav.vehicleType),
      tripType: fav.tripType || 'team',
      stopoverDurationMinutes: fav.stopoverDurationMinutes || 30,
      insertPosition: favInsertPosition,
      vehicleAssignmentId: vehicleAssignments[0]?.id,
      clubId: undefined,
      customLocation: fav.customLocation || undefined
    });
    setUseClubAddress(false);
    setActiveTab('empty');
  };

  const handleDeleteFavorite = async (id: number) => {
    try {
      await matchdayPlanningApi.deleteFavorite(id);
      setFavorites(prev => prev.filter(f => f.id !== id));
    } catch (err) {
      console.error('Error deleting favorite:', err);
      alert('Fehler beim Löschen des Favoriten');
    }
  };

  const handleApplyTemplate = async (templateId: number) => {
    if (!planningId) return;
    if (!confirm('Vorlage anwenden? Bestehende Segmente (außer Anstoß) werden entfernt.')) return;
    try {
      await matchdayPlanningApi.applyPlanningTemplate({
        planningTemplateId: templateId,
        targetPlanningId: planningId
      });
      onRefreshPlanning?.();
      onClose();
    } catch (err) {
      console.error('Error applying template:', err);
      alert('Fehler beim Anwenden der Vorlage');
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    try {
      await matchdayPlanningApi.deletePlanningTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('Error deleting template:', err);
      alert('Fehler beim Löschen der Vorlage');
    }
  };

  const handleCopyFromPlanning = async () => {
    if (!planningId || !selectedCopyPlanningId) return;
    if (!confirm('Segmente aus dieser Planung kopieren? Bestehende Segmente (außer Anstoß) werden entfernt.')) return;
    try {
      await matchdayPlanningApi.copyFromPlanning({
        sourcePlanningId: selectedCopyPlanningId,
        targetPlanningId: planningId
      });
      onRefreshPlanning?.();
      onClose();
    } catch (err) {
      console.error('Error copying from planning:', err);
      alert('Fehler beim Kopieren der Segmente');
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!planningId || !templateName.trim()) return;
    try {
      await matchdayPlanningApi.createPlanningTemplate({
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        sourcePlanningId: planningId
      });
      alert('Vorlage gespeichert!');
      setSavingTemplateMode(false);
      setTemplateName('');
      setTemplateDescription('');
      // Reload templates
      const data = await matchdayPlanningApi.getPlanningTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Error saving template:', err);
      alert('Fehler beim Speichern der Vorlage');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content segment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {mode === 'create' ? 'Neues Segment hinzufügen' : 'Segment bearbeiten'}
          </h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Tabs for create mode */}
        {mode === 'create' && (
          <div className="segment-modal-tabs">
            <button className={`tab-btn ${activeTab === 'empty' ? 'active' : ''}`} onClick={() => setActiveTab('empty')}>Leer</button>
            <button className={`tab-btn ${activeTab === 'favorites' ? 'active' : ''}`} onClick={() => setActiveTab('favorites')}>Favoriten</button>
            <button className={`tab-btn ${activeTab === 'copy' ? 'active' : ''}`} onClick={() => setActiveTab('copy')}>Spieltag kopieren</button>
            <button className={`tab-btn ${activeTab === 'templates' ? 'active' : ''}`} onClick={() => setActiveTab('templates')}>Vorlagen</button>
          </div>
        )}

        {/* Tab: Favorites */}
        {mode === 'create' && activeTab === 'favorites' && (
          <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {tabLoading ? (
              <p style={{ textAlign: 'center', padding: '1rem' }}>Laden...</p>
            ) : favorites.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '1rem', color: '#6c757d' }}>Keine Favoriten gespeichert. Speichere Segmente als Favoriten über das Stern-Symbol.</p>
            ) : (
              <>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label>Einfügen:</label>
                  <div className="game-position-toggle">
                    <div className={`position-option before ${favInsertPosition === 'before-game' ? 'active' : ''}`} onClick={() => setFavInsertPosition('before-game')}>
                      <span className="arrow-before">→</span><span className="ball">Spiel</span>
                    </div>
                    <div className={`position-option after ${favInsertPosition === 'after-game' ? 'active' : ''}`} onClick={() => setFavInsertPosition('after-game')}>
                      <span className="ball">Spiel</span><span className="arrow-after">→</span>
                    </div>
                  </div>
                </div>
                {favorites.map(fav => (
                  <div key={fav.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #e9ecef', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <strong>{fav.name}</strong>
                      <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        {ANCHOR_CONFIGS[fav.anchorType]?.name || fav.anchorType} · {fav.location} · {fav.offsetMinutes > 0 ? '+' : ''}{fav.offsetMinutes} min
                      </div>
                    </div>
                    <button type="button" className="btn btn-primary" style={{ padding: '4px 12px', fontSize: '0.85rem' }} onClick={() => handleLoadFavoriteIntoForm(fav)}>Bearbeiten</button>
                    <button type="button" className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.85rem' }} onClick={() => handleDeleteFavorite(fav.id)} title="Löschen">×</button>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Tab: Copy from Matchday */}
        {mode === 'create' && activeTab === 'copy' && (
          <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {tabLoading ? (
              <p style={{ textAlign: 'center', padding: '1rem' }}>Laden...</p>
            ) : allPlannings.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '1rem', color: '#6c757d' }}>Keine anderen Planungen vorhanden.</p>
            ) : (
              <>
                <div className="form-group">
                  <label>Planung auswählen:</label>
                  <select className="form-select" value={selectedCopyPlanningId || ''} onChange={(e) => setSelectedCopyPlanningId(e.target.value ? parseInt(e.target.value) : undefined)}>
                    <option value="">-- Planung wählen --</option>
                    {allPlannings.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.match?.homeClub?.shortName || '?'} vs {p.match?.awayClub?.shortName || '?'})
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '8px', display: 'block' }}>
                    Alle Segmente (außer Anstoß) werden aus der gewählten Planung kopiert. Bestehende Segmente werden entfernt.
                  </small>
                </div>
                <div style={{ textAlign: 'right', marginTop: '12px' }}>
                  <button type="button" className="btn btn-primary" disabled={!selectedCopyPlanningId} onClick={handleCopyFromPlanning}>Kopieren</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Tab: Templates */}
        {mode === 'create' && activeTab === 'templates' && (
          <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {tabLoading ? (
              <p style={{ textAlign: 'center', padding: '1rem' }}>Laden...</p>
            ) : (
              <>
                {/* Save current planning as template */}
                {planningId && (
                  <div style={{ padding: '12px', borderBottom: '2px solid #dee2e6', marginBottom: '8px' }}>
                    {!savingTemplateMode ? (
                      <button type="button" className="btn btn-secondary" style={{ width: '100%' }} onClick={() => setSavingTemplateMode(true)}>
                        Aktuelle Planung als Vorlage speichern
                      </button>
                    ) : (
                      <div>
                        <div className="form-group" style={{ marginBottom: '8px' }}>
                          <input type="text" placeholder="Name der Vorlage" value={templateName} onChange={(e) => setTemplateName(e.target.value)} style={{ width: '100%' }} />
                        </div>
                        <div className="form-group" style={{ marginBottom: '8px' }}>
                          <input type="text" placeholder="Beschreibung (optional)" value={templateDescription} onChange={(e) => setTemplateDescription(e.target.value)} style={{ width: '100%' }} />
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button type="button" className="btn btn-primary" onClick={handleSaveAsTemplate} disabled={!templateName.trim()}>Speichern</button>
                          <button type="button" className="btn btn-secondary" onClick={() => setSavingTemplateMode(false)}>Abbrechen</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {templates.length === 0 ? (
                  <p style={{ textAlign: 'center', padding: '1rem', color: '#6c757d' }}>Keine Vorlagen vorhanden.</p>
                ) : (
                  templates.map(tmpl => (
                    <div key={tmpl.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #e9ecef', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <strong>{tmpl.name}</strong>
                        {tmpl.description && <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>{tmpl.description}</div>}
                        <div style={{ fontSize: '0.8rem', color: '#adb5bd' }}>{tmpl.segments?.length || 0} Segmente</div>
                      </div>
                      <button type="button" className="btn btn-primary" style={{ padding: '4px 12px', fontSize: '0.85rem' }} onClick={() => handleApplyTemplate(tmpl.id)}>Anwenden</button>
                      <button type="button" className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.85rem' }} onClick={() => handleDeleteTemplate(tmpl.id)} title="Löschen">×</button>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        )}

        {/* Tab: Empty (standard form) - also shown in edit mode */}
        {(mode === 'edit' || activeTab === 'empty') && (
        <>
        <form onSubmit={handleSubmit} className="modal-body">
          {/* Club Selection (optional) */}
          <div className="form-group">
            <label htmlFor="clubId">Verein (optional):</label>
            <select
              id="clubId"
              value={formData.clubId || ''}
              onChange={(e) => {
                const clubId = e.target.value ? parseInt(e.target.value) : undefined;
                handleInputChange('clubId', clubId);
                setUseClubAddress(!!clubId);
              }}
              className="form-select"
            >
              <option value="">-- Kein Verein --</option>
              {clubs.map(club => (
                <option key={club.id} value={club.id}>
                  {club.name} ({club.city})
                </option>
              ))}
            </select>
            {formData.clubId && (
              <div style={{ marginTop: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }}>
                  <input
                    type="checkbox"
                    checked={useClubAddress}
                    onChange={(e) => setUseClubAddress(e.target.checked)}
                  />
                  Vereinsadresse als Ort verwenden
                </label>
              </div>
            )}
          </div>

          {/* Location Input */}
          <div className="form-group">
            <label htmlFor="location">
              {formData.vehicleType === 'plane' ? <><Plane size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Flugnummer:</> : 'Ort / Route:'}
            </label>
            <input
              type="text"
              id="location"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder={formData.vehicleType === 'plane' ? 'z.B. LH1234 oder EW9876' : 'z.B. Bremen Stadion'}
              required
              disabled={useClubAddress && formData.vehicleType !== 'plane'}
              style={{
                backgroundColor: useClubAddress && formData.vehicleType !== 'plane' ? '#f8f9fa' : 'white',
                cursor: useClubAddress && formData.vehicleType !== 'plane' ? 'not-allowed' : 'text'
              }}
            />
            {formData.vehicleType === 'plane' && (
              <small style={{ color: '#3b82f6', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                <Plane size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Geben Sie die Flugnummer ein (z.B. LH1234, EW567, FR8901). Diese wird für das Live-Tracking des Fluges verwendet.
              </small>
            )}
            {useClubAddress && formData.vehicleType !== 'plane' && (
              <small style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                Adresse wird automatisch vom ausgewählten Verein übernommen
              </small>
            )}
          </div>

          {/* Address for route calculation (optional) */}
          <div className="form-group">
            <label htmlFor="customLocation">Adresse (für Routenberechnung):</label>
            <input
              type="text"
              id="customLocation"
              value={formData.customLocation || ''}
              onChange={(e) => handleInputChange('customLocation', e.target.value)}
              placeholder="z.B. Sülzgürtel 2, 50937 Köln"
            />
            <small style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
              Genaue Adresse für die TomTom-Routenberechnung. Wenn leer, wird der Ortsname oben verwendet.
            </small>
          </div>

          {/* Date and Time Input */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="scheduledDate">Datum:</label>
              <input
                type="date"
                id="scheduledDate"
                value={formData.scheduledDate}
                onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="scheduledTime">Uhrzeit:</label>
              <input
                type="time"
                id="scheduledTime"
                value={formData.scheduledTime}
                onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Position Selection (only for create mode) */}
          {mode === 'create' && (
            <div className="form-group">
              <label>Position:</label>
              <div className="game-position-toggle">
                <div
                  className={`position-option before ${formData.insertPosition === 'before-game' ? 'active' : ''}`}
                  onClick={() => handlePositionChange('before-game')}
                  title="Vor dem Spiel"
                >
                  <span className="arrow-before">→</span>
                  <span className="ball">Spiel</span>
                </div>
                <div
                  className={`position-option after ${formData.insertPosition === 'after-game' ? 'active' : ''}`}
                  onClick={() => handlePositionChange('after-game')}
                  title="Nach dem Spiel"
                >
                  <span className="ball">Spiel</span>
                  <span className="arrow-after">→</span>
                </div>
              </div>
            </div>
          )}

          {/* Trip Type Toggle */}
          <div className="form-group">
            <label>Fahrttyp:</label>
            <div className="modal-trip-type-toggle">
              <div
                className={`modal-toggle-field team ${formData.tripType === 'team' ? 'active' : ''}`}
                onClick={() => handleTripTypeChange('team')}
              >
                Team
              </div>
              <div
                className={`modal-toggle-field empty ${formData.tripType === 'empty' ? 'active' : ''}`}
                onClick={() => handleTripTypeChange('empty')}
              >
                Leer
              </div>
            </div>
          </div>

          {/* Anchor Type Toggle */}
          <div className="form-group">
            <label>Anker-Typ:</label>
            <div className="modal-anchor-type-toggle">
              {Object.values(ANCHOR_CONFIGS).filter(config => config.type !== 'main').map((config) => (
                <div
                  key={config.type}
                  className={`modal-anchor-field ${config.type} ${formData.anchorType === config.type ? 'active' : ''}`}
                  onClick={() => handleAnchorTypeChange(config.type)}
                  title={config.name}
                  style={{
                    backgroundColor: formData.anchorType === config.type ? config.color : undefined,
                    color: formData.anchorType === config.type ? 'white' : undefined
                  }}
                >
                  {config.name}
                </div>
              ))}
            </div>
          </div>

          {/* Vehicle Type Selection (hidden for stopover/main) */}
          {formData.anchorType !== 'stopover' && formData.anchorType !== 'main' && (
            <div className="form-group">
              <label>Verkehrsmittel:</label>
              {(() => {
                const vehicleConfig = VEHICLE_CONFIGS[formData.vehicleType] || VEHICLE_CONFIGS['bus'];
                return (
                  <button
                    type="button"
                    className="vehicle-select-btn-modal"
                    onClick={() => setIsVehicleModalOpen(true)}
                    style={{
                      border: `2px solid ${vehicleConfig.color}`,
                      borderRadius: '8px',
                      fontWeight: '600',
                      backgroundColor: 'white',
                      color: '#495057',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      width: '100%',
                      fontSize: '1rem'
                    }}
                  >
                    <span>{vehicleConfig.name}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#6c757d' }}>▼</span>
                  </button>
                );
              })()}
            </div>
          )}

          {/* Passenger Count (hidden for stopover/main) */}
          {formData.anchorType !== 'stopover' && formData.anchorType !== 'main' && (
            <div className="form-group">
              <label htmlFor="passengerCount">Passagiere (für CO₂e-Berechnung):</label>
              <input
                type="number"
                id="passengerCount"
                value={formData.passengerCount ?? ''}
                onChange={(e) => handleInputChange('passengerCount', e.target.value ? parseInt(e.target.value) : undefined)}
                min="1"
                max="1000"
                placeholder="Standard: Fahrzeugkapazität"
              />
              <small style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                Anzahl der reisenden Personen. Wenn leer, wird die Kapazität des zugeordneten Fahrzeugs verwendet.
              </small>
            </div>
          )}

          {/* Stopover Duration */}
          {formData.anchorType === 'stopover' && (
            <div className="form-group" id="stopoverDurationGroup">
              <label htmlFor="stopoverDuration">Aufenthaltsdauer:</label>
              <div className="duration-input-group">
                <input
                  type="number"
                  id="stopoverDuration"
                  value={formData.stopoverDurationMinutes}
                  onChange={(e) => handleInputChange('stopoverDurationMinutes', parseInt(e.target.value))}
                  min="5"
                  max="1440"
                />
                <span className="duration-unit">min</span>
              </div>
            </div>
          )}

          {/* Vehicle Assignment Selection (hidden for stopover/main) */}
          {vehicleAssignments.length > 0 && formData.anchorType !== 'stopover' && formData.anchorType !== 'main' && (
            <div className="form-group">
              <label htmlFor="vehicleAssignmentId"><Bus size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Fahrzeugzuordnung (optional):</label>
              <select
                id="vehicleAssignmentId"
                value={formData.vehicleAssignmentId ?? ''}
                onChange={(e) => handleVehicleAssignmentChange(e.target.value ? parseInt(e.target.value) : undefined)}
                className="form-select"
              >
                <option value="">-- Keine Zuordnung --</option>
                {vehicleAssignments.map(va => (
                  <option key={va.id} value={va.id}>
                    {getAssignmentLabel(va)}
                  </option>
                ))}
              </select>
              <small style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                Wählen Sie das Fahrzeug aus dem Fahrzeugplan, das dieses Segment übernimmt.
              </small>
            </div>
          )}

        </form>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="btn btn-primary" onClick={handleSubmit}>
            {mode === 'create' ? 'Hinzufügen' : 'Speichern'}
          </button>
        </div>
        </>
        )}

        {/* Footer for non-form tabs: just close button */}
        {mode === 'create' && activeTab !== 'empty' && (
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Schließen
            </button>
          </div>
        )}

        {isVehicleModalOpen && (
          <VehicleSelectionModal
            currentVehicle={formData.vehicleType}
            onSelect={handleVehicleTypeChange}
            onClose={() => setIsVehicleModalOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default SegmentModal;
